/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  forwardRef,
  ElementRef,
  NgZone,
  Renderer2,
  ChangeDetectorRef,
  booleanAttribute
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { UmssLabelComponent } from '../label/label.component';
import { UmssToggleButtonComponent } from '../toggle-button/toggle-button.component';

@Component({
  selector: 'umss-button-group',
  standalone: true,
  imports: [CommonModule, UmssLabelComponent],
  templateUrl: './button-group.component.html',
  styleUrls: ['./button-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssButtonGroupComponent),
      multi: true
    }
  ]
})
export class UmssButtonGroupComponent
  implements ControlValueAccessor, AfterContentInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';

  @ContentChildren(UmssToggleButtonComponent)
  buttons!: QueryList<UmssToggleButtonComponent>;

  @Input() size?: 'Mini' | 'Small' | 'Regular' | 'Large';
  @Input() variant?: 'outlined' | 'ghost';
  @Input() label?: string;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() allowWrap: boolean = true;
  @Input() multiple: boolean = false;
  @Input() clearable: boolean = true;
  @Input() maxPerRow?: number;
  @Input() checkmarkPosition: 'off' | 'left' | 'right' = 'right';
  @Input() rowGap: number = 4; // Vertical gap between rows when wrapping
  @Input({ transform: booleanAttribute }) requireSelection: boolean = false;

  get _labelSize(): 'Small' | 'Regular' | 'Large' {
    return this.size === 'Mini' || this.size === 'Small' ? 'Small' : 'Regular';
  }

  @Output() valueChange = new EventEmitter<unknown>();
  @Output() selectionChange = new EventEmitter<unknown>();

  /** Logical columns per row (for Left/Middle/Right position assignment) */
  _resolvedMaxPerRow = 1;
  /** LCM-based CSS grid column count so the last row fills full container width */
  _resolvedGridCols = 1;
  /** True once the first valid layout has been computed; hides buttons until then */
  _layoutReady = false;

  private _resizeObserver?: ResizeObserver;
  private _roTimeout?: ReturnType<typeof setTimeout>;
  private _totalMinWidth = 0;
  private _maxButtonWidth = 0;

  constructor(
    private hostRef: ElementRef,
    private ngZone: NgZone,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  private _value: unknown = null;
  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};
  private _subscriptions: Subscription[] = [];
  private _changesSubscription?: Subscription;
  private _focusedIndex = -1;

  /** Current value (read-only accessor for external use) */
  get value(): unknown {
    return this._value;
  }

  // ControlValueAccessor
  writeValue(value: unknown): void {
    if (this.multiple) {
      if (Array.isArray(value)) this._value = value;
      else if (value != null) this._value = [value];
      else this._value = [];
    } else {
      this._value = value ?? null;
    }
    this._syncButtonStates();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._propagateDisabled();
  }

  ngAfterContentInit(): void {
    this._changesSubscription = this.buttons.changes
      .pipe(startWith(this.buttons))
      .subscribe(() => this._setupButtons());
  }

  ngOnDestroy(): void {
    this._cleanupSubscriptions();
    this._changesSubscription?.unsubscribe();
    this._destroyResizeObserver();
  }

  /** Keyboard navigation: ArrowLeft/ArrowRight to move focus, Enter/Space to select */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    const buttonsArray = this.buttons?.toArray();
    if (!buttonsArray || buttonsArray.length === 0) return;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this._navigateButtons(1, buttonsArray);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this._navigateButtons(-1, buttonsArray);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (
          this._focusedIndex >= 0 &&
          this._focusedIndex < buttonsArray.length
        ) {
          this._selectButton(buttonsArray[this._focusedIndex]);
        }
        break;
      case 'Home':
        event.preventDefault();
        this._focusButton(0, buttonsArray);
        break;
      case 'End':
        event.preventDefault();
        this._focusButton(buttonsArray.length - 1, buttonsArray);
        break;
    }
  }

  /** Check if a specific value is currently selected */
  isSelected(val: unknown): boolean {
    if (this.multiple && Array.isArray(this._value))
      return this._value.includes(val);

    return this._value === val && this._value != null;
  }

  /** Programmatically clear the selection */
  clear(): void {
    this._value = this.multiple ? [] : null;
    this._syncButtonStates();
    this._onChange(this._value);
    this._onTouched();
    this.valueChange.emit(this._value);
    this.selectionChange.emit(this._value);
  }

  private _setupButtons(): void {
    this._cleanupSubscriptions();
    this._layoutReady = false;

    // Defer so the DOM is ready, then attempt layout immediately rather than
    // waiting for the ResizeObserver's first async notification (~1 frame later).
    setTimeout(() => {
      this._measureIntrinsicWidth();
      if (this.allowWrap) {
        this._initResizeObserver();
        // Attempt layout right now using getBoundingClientRect (synchronous).
        // If the container already has a real width (most cases, including
        // transform-only animations like BottomSheet), this sets _layoutReady=true
        // immediately — no visible flash. If width is 0 (hidden / clip-animating),
        // skip and let ResizeObserver handle it when the container becomes visible.
        const container = this.hostRef.nativeElement.querySelector(
          '.button-group'
        ) as HTMLElement | null;
        const immediateWidth = container?.getBoundingClientRect().width ?? 0;
        if (immediateWidth > 0) {
          this._computeResponsiveWrap(immediateWidth);
        }
      } else {
        this._destroyResizeObserver();
        this._applyLayout(this.buttons.length);
      }
    }, 0);

    this._propagateProperties();
    this._syncButtonStates();
    this._subscribeToClicks();
  }

  /** Measure total intrinsic width (fits-in-one-row check) and widest individual button (column-fit guarantee). */
  private _measureIntrinsicWidth(): void {
    const container = this.hostRef.nativeElement.querySelector('.button-group');
    if (!container) return;

    // Temporarily switch to inline-flex to measure natural widths
    this.renderer.setStyle(container, 'display', 'inline-flex');
    this.renderer.setStyle(container, 'width', 'max-content');

    this._totalMinWidth = container.offsetWidth;

    // Widest individual button — ensures no button clips its content in any column
    let maxWidth = 0;
    this.buttons.forEach((btn) => {
      const btnEl: HTMLElement | null =
        btn.elementRef.nativeElement.querySelector('button');
      if (btnEl) maxWidth = Math.max(maxWidth, btnEl.offsetWidth);
    });
    this._maxButtonWidth = maxWidth;

    this.renderer.removeStyle(container, 'display');
    this.renderer.removeStyle(container, 'width');
  }

  private _initResizeObserver(): void {
    if (this._resizeObserver) return;
    this.ngZone.runOutsideAngular(() => {
      this._resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          clearTimeout(this._roTimeout);
          this._roTimeout = setTimeout(() => {
            this.ngZone.run(() => {
              this._computeResponsiveWrap(entry.contentRect.width);
            });
          }, 10);
        }
      });
      const container =
        this.hostRef.nativeElement.querySelector('.button-group');
      if (container) this._resizeObserver.observe(container);
    });
  }

  private _destroyResizeObserver(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    clearTimeout(this._roTimeout);
  }

  private _computeResponsiveWrap(containerWidth: number): void {
    const count = this.buttons.length;

    // Container has no width yet (hidden or clipped during an animation that
    // affects dimensions rather than transform). Skip rather than computing a
    // 1-column fallback that would need correcting a frame later.
    if (containerWidth === 0) return;

    // If measurement failed (e.g. rendered during animation), re-measure
    if (this._totalMinWidth === 0 && count > 0) {
      this._measureIntrinsicWidth();
    }

    // If we don't need to wrap (plenty of space) or small count
    if (
      count <= 1 ||
      this.maxPerRow ||
      (this._totalMinWidth > 0 && containerWidth >= this._totalMinWidth - 2)
    ) {
      this._applyLayout(this.maxPerRow || count);
      return;
    }

    // Needs to wrap — use the widest button width so content always fits in every column
    const refWidth = this._maxButtonWidth || this._totalMinWidth / count;
    let maxFits = Math.floor(containerWidth / refWidth);
    if (maxFits < 1) maxFits = 1;

    const rows = Math.ceil(count / maxFits);
    const balancedCols = Math.ceil(count / rows);
    this._applyLayout(balancedCols);
  }

  private _applyLayout(cols: number): void {
    const count = this.buttons.length;
    const changed = this._resolvedMaxPerRow !== cols;
    this._resolvedMaxPerRow = cols;

    // LCM trick: last (partial) row gets wider spans so it fills the full width.
    // e.g. 9 items, 5 cols → lastRowCount=4, lcm(5,4)=20, grid=repeat(20,1fr)
    //   full rows: each button span 4  (5×4=20)
    //   last row:  each button span 5  (4×5=20)  → same total width ✓
    const lastRowCount = count % cols || cols;
    this._resolvedGridCols =
      lastRowCount < cols ? this._lcm(cols, lastRowCount) : cols;

    const firstLayout = !this._layoutReady;
    this._layoutReady = true;

    // Assign positions first so they're ready before any render.
    this._assignPositions();

    // detectChanges on button-group updates --cols CSS var and reveals the group
    // (opacity: 0 → visible). Must happen after _assignPositions so both the
    // grid template and individual spans/positions are set in one pass.
    if (changed || firstLayout) {
      this.cdr.detectChanges();
    }

    // Toggle-button elements are content-projected — they belong to the host
    // component's view (which may use OnPush), not to button-group's view.
    // button-group's detectChanges() does NOT reach them, so we must trigger
    // change detection on each one explicitly after mutating their @Inputs.
    this.buttons.forEach((b) => b.cdr.detectChanges());
  }

  private _gcd(a: number, b: number): number {
    return b === 0 ? a : this._gcd(b, a % b);
  }

  private _lcm(a: number, b: number): number {
    return (a * b) / this._gcd(a, b);
  }

  private _assignPositions(): void {
    const count = this.buttons.length;
    const cols = this._resolvedMaxPerRow;
    const gridCols = this._resolvedGridCols;

    // Span values for LCM-based last-row filling
    const lastRowCount = count % cols || cols;
    const firstLastRowIndex = count - lastRowCount;
    const normalSpan = gridCols / cols;
    const lastSpan = gridCols / lastRowCount;

    const totalRows = Math.ceil(count / cols);
    const isConnected = this.rowGap === 0 && totalRows > 1;

    this.buttons.forEach((button, index) => {
      // Set grid-column span — last row gets wider spans to fill container width
      button.gridSpan = index >= firstLastRowIndex ? lastSpan : normalSpan;

      // Vertical position for connected rows (rowGap=0): fixes border-radius and double-border
      if (isConnected) {
        const rowIndex = Math.floor(index / cols);
        if (rowIndex === 0) button.rowPosition = 'first';
        else if (rowIndex === totalRows - 1) button.rowPosition = 'last';
        else button.rowPosition = 'middle';
      } else {
        button.rowPosition = 'only';
      }

      // Assign border-radius position (Left / Middle / Right / Single)
      if (this.allowWrap && cols > 0 && cols < count) {
        const posInRow = index % cols;
        const itemsInThisRow = Math.min(
          cols,
          count - Math.floor(index / cols) * cols
        );

        if (itemsInThisRow === 1) button.position = 'Single';
        else if (posInRow === 0) button.position = 'Left';
        else if (posInRow === itemsInThisRow - 1) button.position = 'Right';
        else button.position = 'Middle';
      } else {
        if (count === 1) button.position = 'Single';
        else if (index === 0) button.position = 'Left';
        else if (index === count - 1) button.position = 'Right';
        else button.position = 'Middle';
      }
    });
  }

  private _propagateProperties(): void {
    this.buttons.forEach((button) => {
      if (this.size) button.size = this.size;
      if (this.variant) button.variant = this.variant;
      button.checkmarkPosition = this.checkmarkPosition;
    });
    this._propagateDisabled();
  }

  private _propagateDisabled(): void {
    if (this.buttons) {
      this.buttons.forEach((button) => {
        button.disabled = this.disabled;
      });
    }
  }

  private _syncButtonStates(): void {
    if (this.buttons) {
      this.buttons.forEach((button) => {
        if (this.multiple && Array.isArray(this._value)) {
          button.pressed = this._value.includes(button.value);
        } else {
          button.pressed = button.value === this._value && this._value != null;
        }
      });
    }
  }

  private _subscribeToClicks(): void {
    this.buttons.forEach((button) => {
      const sub = button.onClick.subscribe(() => {
        this._selectButton(button);
      });
      this._subscriptions.push(sub);
    });
  }

  private _selectButton(selected: UmssToggleButtonComponent): void {
    if (this.disabled) return;

    if (this.multiple) {
      const current = Array.isArray(this._value) ? [...this._value] : [];
      const idx = current.indexOf(selected.value);
      if (idx >= 0) {
        if (this.requireSelection && current.length === 1) return;
        current.splice(idx, 1);
      } else {
        current.push(selected.value);
      }
      this._value = current.length > 0 ? current : [];
    } else {
      // Toggle off if clicking the already-selected button (clearable behavior)
      if (
        this.clearable &&
        !this.requireSelection &&
        this._value === selected.value
      ) {
        this._value = null;
      } else {
        this._value = selected.value;
      }
    }

    this._syncButtonStates();
    this._onChange(this._value);
    this._onTouched();
    this.valueChange.emit(this._value);
    this.selectionChange.emit(this._value);
  }

  private _navigateButtons(
    direction: number,
    buttonsArray: UmssToggleButtonComponent[]
  ): void {
    const enabledButtons = buttonsArray.filter((b) => !b.disabled);
    if (enabledButtons.length === 0) return;

    // Find current focused index within enabled buttons
    const currentEnabled =
      this._focusedIndex >= 0
        ? enabledButtons.indexOf(buttonsArray[this._focusedIndex])
        : -1;

    let newIndex = currentEnabled + direction;
    if (newIndex < 0) newIndex = enabledButtons.length - 1;
    if (newIndex >= enabledButtons.length) newIndex = 0;

    const globalIndex = buttonsArray.indexOf(enabledButtons[newIndex]);
    this._focusButton(globalIndex, buttonsArray);
  }

  private _focusButton(
    index: number,
    buttonsArray: UmssToggleButtonComponent[]
  ): void {
    this._focusedIndex = index;
    const button = buttonsArray[index];
    if (button) {
      const nativeButton =
        button.elementRef.nativeElement?.querySelector('button');
      nativeButton?.focus();
    }
  }

  private _cleanupSubscriptions(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
    this._subscriptions = [];
  }
}
